'use client';

// Shared UI primitives for the logged-in app shell

// ── Avatar ────────────────────────────────────────────────────
// If a photo is provided, render the image on top of the seeded gradient
// fallback; if the image fails to load (or none is provided) the gradient
// + initials show through, so we never get a fully blank circle.
//
// Pass both `src` (full) and `thumbSrc` (256² crop) — the small renders
// (sidebar/lists) use the thumb to save bandwidth, large renders fall
// back to the full image. Either prop works on its own too.
const THUMB_BREAKPOINT = 96;

export function Avatar({ src, thumbSrc, seed = 'a', size = 40, square = false }: {
  src?: string | null;
  thumbSrc?: string | null;
  seed?: string;
  size?: number;
  square?: boolean;
}) {
  const chosen = size <= THUMB_BREAKPOINT ? (thumbSrc ?? src) : (src ?? thumbSrc);
  const hash = [...seed].reduce((a, c) => a + c.charCodeAt(0), 0);
  const h1 = (hash * 37) % 360;
  const h2 = (h1 + 60) % 360;
  const initials = seed.slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, flexShrink: 0,
      borderRadius: square ? size * 0.28 : '50%',
      background: `linear-gradient(135deg, oklch(70% 0.16 ${h1}), oklch(58% 0.18 ${h2}))`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', fontWeight: 700, fontSize: size * 0.34,
      overflow: 'hidden',
      position: 'relative',
    }}>
      {initials}
      {chosen && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={chosen}
          alt=""
          loading="lazy"
          style={{ position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      )}
    </div>
  );
}

// ── AvatarBlob ────────────────────────────────────────────────
// "Iridescent" placeholder for anonymous avatars (no photo): a
// conic-gradient circle seeded by a string, with a soft white
// highlight to suggest a face. Callers wrap it in their own photo
// fallback (`{photo ? <img/> : <AvatarBlob seed={name} size={…} />}`).

export function AvatarBlob({ seed, size = 44 }: { seed: string; size?: number }) {
  const hue = ([...seed].reduce((a, c) => a + c.charCodeAt(0), 0) * 37) % 360;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      overflow: 'hidden', position: 'relative',
      background: `conic-gradient(from 120deg, oklch(60% 0.2 ${hue}), oklch(40% 0.18 ${(hue + 80) % 360}), oklch(55% 0.22 ${(hue + 180) % 360}), oklch(60% 0.2 ${hue}))`,
    }}>
      <div style={{
        position: 'absolute', inset: '30%', borderRadius: '50%',
        background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,.5), transparent 60%)',
      }} />
    </div>
  );
}

// ── Icon ──────────────────────────────────────────────────────
type IconName = 'home'|'search'|'plus'|'heart'|'chat'|'send'|'bookmark'|'wallet'|'bolt'|'bell'|'play'|'chart'|'arrowUp'|'chevronRight'|'close'|'check'|'lock'|'more'|'coin'|'edit'|'signOut'|'shield'|'user'|'grid'|'settings'|'image';

const PATHS: Record<IconName, React.ReactNode> = {
  home:    <><path d="M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2v-9z"/></>,
  search:  <><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></>,
  plus:    <><path d="M12 5v14M5 12h14"/></>,
  heart:   <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></>,
  chat:    <><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></>,
  send:    <><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></>,
  bookmark:<><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></>,
  wallet:  <><path d="M3 7h18v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"/><path d="M3 7l3-4h12l3 4M16 13h2"/></>,
  bolt:    <><path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z"/></>,
  bell:    <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
  play:    <><polygon points="5 3 19 12 5 21" fill="currentColor"/></>,
  chart:   <><path d="M3 3v18h18"/><path d="M7 14l3-3 4 4 5-5"/></>,
  arrowUp: <><path d="M12 19V5M5 12l7-7 7 7"/></>,
  chevronRight: <><path d="M9 18l6-6-6-6"/></>,
  close:   <><path d="M18 6L6 18M6 6l12 12"/></>,
  check:   <><path d="M20 6L9 17l-5-5"/></>,
  lock:    <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
  more:    <><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></>,
  coin:    <><circle cx="12" cy="12" r="9"/><path d="M12 6v12M9 9h4.5a2.5 2.5 0 0 1 0 5H9M9 14h5"/></>,
  edit:    <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
  signOut: <><path d="M14 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7a2 2 0 0 0 2-2v-3M10 12h11M21 12l-3-3M21 12l-3 3"/></>,
  shield:  <><path d="M12 2l9 4v6c0 5-3.5 9.4-9 10-5.5-.6-9-5-9-10V6l9-4z"/></>,
  user:    <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></>,
  grid:    <><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></>,
  settings:<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9"/></>,
  image:   <><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="1.6"/><path d="M21 15l-5-5L5 21"/></>,
};

export function Icon({ name, size = 20, stroke = 'currentColor', fill = 'none' }: { name: IconName; size?: number; stroke?: string; fill?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {PATHS[name] || null}
    </svg>
  );
}

